/* eslint-disable brace-style */

import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserProxy } from '../store/user/user.proxy';
import { getWindow } from 'ssr-window';
import { HelperService } from '../services/helper.service';
import { debug } from 'console';
import { productFruits } from 'product-fruits';

const PUBLIC_PAGES = ['page', 'sl'];

@Injectable({ providedIn: 'root' })
export class AuthService {

  isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
  isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$
  ]).pipe(map(values => values.every(b => b)));

  constructor(
    private oauthService: OAuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userProxy: UserProxy,
    private helperService: HelperService
  ) {
    this.handleSsoLogin();

    // Useful for debugging:
    // this.oauthService.events.subscribe(event => {
    //   if (event instanceof OAuthErrorEvent) {
    //     console.error('OAuthErrorEvent Object:', event);
    //   } else {
    //     console.warn('OAuthEvent Object:', event);
    //   }
    // });

    // This is tricky, as it might cause race conditions (where access_token is set in another
    // tab before everything is said and done there.
    // TODO: Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    getWindow().addEventListener('storage', (event) => {
      console.log('storage event', event);
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.log('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      setTimeout(() => {
        this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
        if (!this.oauthService.hasValidAccessToken()) {
          this.activatedRoute.queryParams.pipe(take(1)).subscribe(queryParams => {
            const stateUrl = queryParams && queryParams['stateUrl'] ? queryParams['stateUrl'] : this.router.url;
            this.userProxy.userEmail$.pipe(take(1)).subscribe(userEmail => {
              const email = queryParams && queryParams['email'] ? queryParams['email'] : userEmail;
              this.router.navigate(['./logout-listener'], { queryParams: { stateUrl, email }});
            })
          });
        }
      }, 500);
    });

    this.oauthService.events.subscribe((event) => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    // this.oauthService.events
    //   .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
    //   .subscribe(e => this.login());

    this.oauthService.setupAutomaticSilentRefresh();
  }

  runInitialLoginSequence(): Promise<void> {    
    if (getWindow().location.hash.includes('sso')) {
      getWindow().addEventListener('message', this.handleSsoMessage);
      return Promise.resolve();
    }

    // Check for plugin authentication parameters
    this.checkAndStorePluginAuthParams();
 
    return this.oauthService.loadDiscoveryDocument(environment.oauthDiscoveryDocument)
      .then(() => this.oauthService.tryLogin())
      .then(() => {

        return this.oauthService.hasValidAccessToken() ? this.proceedLogin() : Promise.reject();
      })
      .catch(() => { 
        this.isDoneLoadingSubject$.next(true) });
  }

  private proceedLogin() {
  if (this.helperService.isBrowser) {
    
    this.createHiddenIframeAndSetCookie();
  }
  this.isDoneLoadingSubject$.next(true);
  this.userProxy.isMaintenance$.next(false);
  return this.userProxy.getUser().then((result: any) => {
    console.log('UserProxy::getUser result', result);
    
    // Check if we need to redirect for plugin authentication
    const pluginRedirect = this.checkAndHandlePluginAuth(result);
    if (pluginRedirect) {
      return;
    }
    
    const redirectTo = result.redirectTo;
    if (redirectTo == 'emailPolicyBreak') {
      this.router.navigate(['email-policy-break']);
      return;
    }
    if (redirectTo === 'blocked') {
      this.router.navigate(['page/account-blocked']);
    } else if (redirectTo === 'maintenance') {
      this.router.navigate(['page/maintenance']);
      this.userProxy.isMaintenance$.next(true);
    } else if (redirectTo === 'disabled') {
      this.router.navigate(['account-disabled']);
    } else {
      console.log('UserProxy::getUser result', result);
            // Initialize Product Fruits with user information
            productFruits.init('YFHV9eXFHj62QEbb', 'en', { 
              username: result.user.email,
              firstname: result.user.name,
              role: result.user.roles[0]
            });

      const currentUrl = getWindow().location.pathname;
      const isPublicPage = PUBLIC_PAGES.some(page => currentUrl.includes(page));
      if (!isPublicPage) {
        if (redirectTo === 'onboarding') {
          console.trace("onboarding");
          this.router.navigate(['onboarding']);
        } else if (currentUrl === '/' || currentUrl.includes('onboarding')) {
          const slRedirect = localStorage.getItem('slRedirect');
          if (slRedirect) {
            localStorage.removeItem('slRedirect');
            this.router.navigate([slRedirect]);
          } else {
            if(localStorage.getItem('onboard')) {
              const onboardUrl = localStorage.getItem('onboard');
              this.router.navigate([onboardUrl]);
            }
              else {
              const role = result.user.roles[0];
              const baseRoute = role === 'talent' ? '/' : '/hm';
              this.router.navigate([baseRoute]);
            }
          }
        }
      }
    }
  });
  }

  private createHiddenIframeAndSetCookie(cookieValue = 'active', cookieName = 'at-sso'): void {
    const iframe: HTMLIFrameElement = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = getWindow().location.origin.includes('localhost') 
      ? `http://localhost:4200/#sso`
      : `${environment.ssoAllowedOrigins}/#sso`;

    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow!.postMessage(
          { sso: { cookieName, cookieValue }},
          getWindow().location.origin.includes('localhost') ? 'http://localhost:4200/' : environment.ssoAllowedOrigins
      );
      setTimeout(() => document.body.removeChild(iframe));
    };
  }

  // private listenOnboardingChanges() {
  //   let result = 'onboarding'
  //   this.router.events.pipe(
  //     filter(event => event instanceof NavigationEnd),
  //   ).subscribe((event: NavigationEnd) => {
  //     const url = event.url;
  //     if (!this.router.url.includes('page') && !this.router.url.includes('sl')) {
  //       let isOnboardingUrl = url.includes('onboarding') || url.includes('onboardComplete');
  //       if (url.includes('onboardComplete')) {
  //         result = 'dashboard';
  //       }
  //       if (result === 'onboarding' && !isOnboardingUrl) {
  //         this.router.navigate(['onboarding']);
  //       } else if (result !== 'onboarding' && isOnboardingUrl) {
  //         this.router.navigate(['dashboard']);
  //       }
  //     }
  //   });
  // }

  // private checkStateExists() {
  //   if (this.oauthService.state && this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
  //     let stateUrl = this.oauthService.state;
  //     if (stateUrl.startsWith('/') === false) {
  //       stateUrl = decodeURIComponent(stateUrl);
  //     }
  //     // console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
  //     this.router.navigateByUrl(stateUrl);
  //   }
  // }

  handleMaintenance() {
    this.userProxy.isMaintenance$.next(true);
    this.router.navigate(['page/maintenance']);
  }

  login(targetUrl?: string) {
    
    console.log('AuthService::login', targetUrl);
    if (this.router.url.includes('onboard/')) {
      const queryParams = new URLSearchParams(targetUrl.split('?')[1]);
      const email = queryParams.get('email');
      const rawState = queryParams.get('state');
      const decodedState = rawState ? decodeURIComponent(rawState) : null;
      this.oauthService.initLoginFlow(decodedState, {
        login_hint: email,
        option:'signup' 
      });
    }
    else if (this.router.url.includes('talent-onboard') || this.router.url.includes('hm-onboard')) {
      const queryParams = new URLSearchParams(targetUrl.split('?')[1]);
      const email = queryParams.get('email');
      const rawState = queryParams.get('state');
      const decodedState = rawState ? decodeURIComponent(rawState) : null;
    this.oauthService.initLoginFlow(decodedState, {
        login_hint: email,
        option:'signup' 
      });
    }
     else {
         this.oauthService.initLoginFlow(targetUrl || this.router.url);
    }
  }

  logout() {
    this.clearClientSessionState();
    this.createHiddenIframeAndSetCookie('logout');
    setTimeout(() => this.oauthService.logOut(), 1000);
  }

  tryRefreshToken() {
    return this.oauthService.refreshToken();
  }
  
  refreshSilent() {
    this.oauthService.silentRefresh().catch(err => {
      console.log('Refresh Silent ERROR', err);
      this.logout();
    });
  }
  
  hasValidToken() { 
    return this.oauthService.hasValidAccessToken(); 
  }

  private handleSsoLogin() {
    const checkSsoLogin = () => {
      const cookieName = 'at-sso';
      const cookies = `; ${getWindow().document.cookie}`;
      const parts = cookies.split(`; ${cookieName}=`);
      const value = parts.length === 2 ? parts.pop().split(';').shift() : null;
      console.log('Sso Login', value);
      if (!this.isAuthenticatedSubject$.getValue()) {
        if (value === 'active') {
          setTimeout(() => this.login());
        }
      } else {
        if (value === 'logout') {
          setTimeout(() => this.logout());
        }
      }
      getWindow().document.cookie = cookieName +'=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    }
    checkSsoLogin();

    getWindow().document.addEventListener('visibilitychange', () => {
      const windowFocused = getWindow().document.visibilityState === 'visible';
      if (windowFocused) {
        checkSsoLogin();
      }
    });
  }

  private handleSsoMessage(event) {
    
    if (!environment.ssoAllowedOrigins.includes(event.origin)) {
      return;
    }

    const { sso } = event.data;
    console.log('Sso frame', sso);
    if (sso) {
      if (sso.cookieName && sso.cookieValue) {
        getWindow().document.cookie = `${sso.cookieName}=${sso.cookieValue};path=/;SameSite=None;Secure`;
      }
    }

    getWindow().removeEventListener('message', this.handleSsoMessage);
  }

  private clearClientSessionState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.removeItem('Atalef:Impersonate');
  }

  private checkAndStorePluginAuthParams(): void {
    try {
      const window = getWindow();
      const urlParams = new URLSearchParams(window.location.search);
      
      const requestedScope = urlParams.get('requestedScope');
      const redirectUri = urlParams.get('redirect_uri');
      
      // Check if this is a plugin authentication request
      if (requestedScope === 'plugin' && redirectUri) {
        console.log('Plugin auth detected - storing parameters in sessionStorage');
        sessionStorage.setItem('plugin_requested_scope', requestedScope);
        sessionStorage.setItem('plugin_redirect_uri', decodeURIComponent(redirectUri));
      }
    } catch (error) {
      console.error('Error storing plugin auth parameters:', error);
    }
  }

  private checkAndHandlePluginAuth(userResult: any): boolean {
    try {
      const window = getWindow();
      
      // Check sessionStorage for plugin auth redirect
      const redirectUri = sessionStorage.getItem('plugin_redirect_uri');
      const requestedScope = sessionStorage.getItem('plugin_requested_scope');
      
      if (!redirectUri || requestedScope !== 'plugin') {
        return false;
      }

      // Get access token
      const accessToken = this.oauthService.getAccessToken();
      if (!accessToken) {
        console.warn('No access token available for plugin redirect');
        sessionStorage.removeItem('plugin_redirect_uri');
        sessionStorage.removeItem('plugin_requested_scope');
        return false;
      }

      // Get token expiration
      const tokenExpiration = this.oauthService.getAccessTokenExpiration();
      const expiresAt = tokenExpiration || Date.now() + 60 * 60 * 1000; // Default to 1 hour if not available

      // Get user email
      const email = userResult?.user?.email || '';

      // Build redirect URL with parameters
      const redirectUrl = new URL(redirectUri);
      redirectUrl.searchParams.set('access_token', accessToken);
      redirectUrl.searchParams.set('expires_at', expiresAt.toString());
      redirectUrl.searchParams.set('email', email);

      console.log('Redirecting to plugin with auth data:', redirectUrl.toString());

      // Clear sessionStorage
      sessionStorage.removeItem('plugin_redirect_uri');
      sessionStorage.removeItem('plugin_requested_scope');

      // Redirect to the plugin callback URL
      window.location.href = redirectUrl.toString();
      
      return true;
    } catch (error) {
      console.error('Error handling plugin auth redirect:', error);
      sessionStorage.removeItem('plugin_redirect_uri');
      sessionStorage.removeItem('plugin_requested_scope');
      return false;
    }
  }

}
